import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { CustomText } from '@/components/CustomText';
import { CustomDivider } from "@/components/CustomDivider";
import { Link } from 'expo-router';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppUtils from '@/utilities/AppUtils';
import { Platform, Pressable, ScrollView, StyleSheet, Text } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { View } from '@/components/Themed';
import Head from 'expo-router/head';
import { StatusBar } from 'expo-status-bar';
import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { Button } from 'react95-native-rabbl';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { CustomSafeAreaView } from '@/components/CustomSafeAreaView';
import { CustomKeyboardAvoidingView } from '@/components/CustomKeyboardAvoidingView';

const TOP_OFFSET = 40;
const GAP = 30;

const styles = StyleSheet.create({
  errorPressable: {
    flex: 1,
  },
  topView: {
    width: "100%",
    height: TOP_OFFSET,
    backgroundColor: "#0000a8",
  },
  errorContainer: {
    flex: 1,
    // https://en.wikipedia.org/wiki/Blue_screen_of_death
    backgroundColor: "#0000a8",
    gap: GAP,
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING_WIDE,
  },
  scrollView: {
    paddingBottom: TOP_OFFSET,
    paddingTop: 0,
    paddingHorizontal: 10,
    alignItems: 'center',
    // https://en.wikipedia.org/wiki/Blue_screen_of_death
    backgroundColor: "#0000a8",
    gap: GAP,
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: GAP,
  },
  errorTitleWrapper: {
    backgroundColor: "#b7b7b7",
    paddingBottom: 6,
    paddingHorizontal: 7,
  },
  errorTitle: {
    // https://stackoverflow.com/questions/38781089/font-family-monospace-monospace
    fontFamily: Platform.OS === "web" ? "monospace, monospace" : "VT323-Regular",
    color: "#0000a8",
    fontSize: 25,
    flexShrink: 1,
  },
  errorTextInheritAlignment: {
    // https://stackoverflow.com/questions/38781089/font-family-monospace-monospace
    fontFamily: Platform.OS === "web" ? "monospace, monospace" : "VT323-Regular",
    color: "#ffffff",
    fontSize: 25,
    flexShrink: 1,
  },
  errorTextLeftAligned: {
    // https://stackoverflow.com/questions/38781089/font-family-monospace-monospace
    fontFamily: Platform.OS === "web" ? "monospace, monospace" : "VT323-Regular",
    color: "#ffffff",
    fontSize: 25,
    flexShrink: 1,
    alignSelf: "flex-start",
  },
  errorButtonText: {
    // https://stackoverflow.com/questions/38781089/font-family-monospace-monospace
    fontFamily: Platform.OS === "web" ? "monospace, monospace" : "VT323-Regular",
    color: "#000000",
    fontSize: 25,
    flexShrink: 1,
    marginBottom: 5,
  },
});

interface CustomErrorBoundaryProps {
  title: string,
  errorMessage: string,
  dismissText: string,
  extraLine?: string,
  error?: Error,
  onDismiss: () => void,
};

export function CustomErrorBoundary({
  title,
  dismissText,
  errorMessage,
  onDismiss,
  error,
  extraLine,
}: CustomErrorBoundaryProps) : React.ReactNode {

  const [successfullyReported, setSuccessfullyReported] = useState(false);

  useEffect(() => {
    if (error) {
      setSuccessfullyReported(false);
      (async () => {
        AppUtilities.handleException(error, "CustomErrorBoundary");
        setSuccessfullyReported(true);
      })();
    }
  }, [error]);

  return (
    <>
      <View style={styles.topView} />
      <ScrollView
        contentContainerStyle={styles.scrollView}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        <SafeAreaProvider>
          <CustomSafeAreaView>
            <CustomKeyboardAvoidingView>
              <View style={styles.errorContainer}>
                <View style={styles.errorTitleWrapper}>
                  <Text style={styles.errorTitle}>{title}</Text>
                </View>
                <Text style={styles.errorTextLeftAligned}>{errorMessage}</Text>
                { successfullyReported && (
                  <Text style={styles.errorTextLeftAligned}>The error has been reported and we'll investigate.</Text>
                )}
                { extraLine && (
                  <Text style={styles.errorTextLeftAligned}>{extraLine}</Text>
                )}
                { extraLine && (
                  <Text style={styles.errorTextLeftAligned}>Time: {new Date().toISOString()}</Text>
                )}
                <View style={[AppConstants.STYLES.padding1, { backgroundColor: "transparent" }]} />
                <Button onPress={onDismiss}><Text style={styles.errorButtonText}>{dismissText}</Text></Button>
                <StatusBar style="light" />
              </View>
            </CustomKeyboardAvoidingView>
          </CustomSafeAreaView>
        </SafeAreaProvider>
      </ScrollView>
    </>
  );
};
